import React, {
  FunctionComponent,
  ChangeEvent,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { AppStore } from "../../../app/store";
import { EnrollmentFormProps } from "./../EnrollmentFormPage";
import BackNextButtons from "./../BackNextButtons";
import { FocusableHeader, SSNInput } from "../../../components";
import {
  TextField,
  ChoiceList,
  DateField,
  HelpDrawerToggle,
  Alert,
} from "@cmsgov/ds-medicare-gov";
import {
  useTranslate,
  dateFormatter,
  sendTealiumEvent,
  getValidationObject,
  EnrollmentPages,
  limitDateInputs,
  isOutsideOpenEnrollment,
} from "../../../helpers";
import MedicareCardHelpDrawer from "../../../components/MedicareCardHelpDrawer";
import PlanInfo from "./../PlanInfo";
import { FormattedMessage } from "react-intl-phraseapp";
import {
  MCTDate,
  PlanType,
  PlanCategoryType,
  PlanSNPType,
} from "../../../@types";
import { SSN } from "../../../components/SSNInput";
import { isInvalidDatePart } from "../../../helpers/dateHelpers";
import { useFlags } from "launchdarkly-react-client-sdk";
import { validateMbi } from "../../../api";
import { isValidMedicareNumber } from "../../../helpers/enrollmentFormHelpers";

const CoverageLossWarning: FunctionComponent<{
  planName: string;
  planType: PlanType;
  category: string;
  snp_type: PlanSNPType;
}> = ({ planName, planType, category, snp_type }) => {
  const t = useTranslate();

  if (snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE) {
    return (
      <>
        <FormattedMessage
          id="enroll_form.coverage_loss_warning.healthcare_from_employer_or_union_disclaimer"
          tagName="p"
          values={{
            plan_name: planName,
          }}
        />
        <p>
          <strong>{t("enroll_form.attention")}</strong>{" "}
          {t(
            "enroll_form.coverage_loss_warning.special_needs_plan.information"
          )}
        </p>
      </>
    );
  }

  if (category === PlanCategoryType.PFFS) {
    return (
      <>
        <p>
          {t("enroll_form.coverage_loss_warning.fee_for_service.information")}
        </p>
        <p>
          {t(
            "enroll_form.coverage_loss_warning.fee_for_service.call_from_plan_representative"
          )}
        </p>
        <FormattedMessage
          id="enroll_form.coverage_loss_warning.healthcare_from_employer_or_union_disclaimer"
          tagName="p"
          values={{
            plan_name: planName,
          }}
        />
      </>
    );
  }

  if (category === PlanCategoryType.COST_1876) {
    return (
      <p>
        {planType !== PlanType.MA && (
          <FormattedMessage
            id="enroll_form.coverage_loss_warning.may_already_have_coverage"
            tagName="span"
            values={{
              plan_name: planName,
            }}
          />
        )}
        <FormattedMessage
          id="enroll_form.coverage_loss_warning.healthcare_from_employer_or_union_disclaimer"
          tagName="span"
          values={{
            plan_name: planName,
          }}
        />
      </p>
    );
  }

  if (planType === PlanType.MAPD || planType === PlanType.MA) {
    return (
      <FormattedMessage
        id="enroll_form.coverage_loss_warning.healthcare_from_employer_or_union_disclaimer"
        tagName="p"
        values={{
          plan_name: planName,
        }}
      />
    );
  } else if (planType === PlanType.PDP) {
    return (
      <>
        <FormattedMessage
          id="enroll_form.coverage_loss_warning.may_already_have_coverage"
          tagName="p"
          values={{
            plan_name: planName,
          }}
        />
        <FormattedMessage
          id="enroll_form.coverage_loss_warning.healthcare_from_employer_or_union_disclaimer"
          tagName="p"
          values={{
            plan_name: planName,
          }}
        />
      </>
    );
  }

  return null;
};

const MedicareInfo: FunctionComponent<EnrollmentFormProps> = ({
  enrollData,
  setEnrollData,
  previousPage,
  nextPage,
  plan,
  inReview,
}) => {
  const t = useTranslate();
  const { state } = useContext(AppStore.AppContext);
  const [helpDrawerOpen, setHelpDrawerOpen] = useState(false);
  const [partAError, setPartAError] = useState<string>();
  const [partADayInvalid, setPartADayInvalid] = useState(false);
  const [partAMonthInvalid, setPartAMonthInvalid] = useState(false);
  const [partAYearInvalid, setPartAYearInvalid] = useState(false);
  const [partBDayInvalid, setPartBDayInvalid] = useState(false);
  const [partBMonthInvalid, setPartBMonthInvalid] = useState(false);
  const [partBYearInvalid, setPartBYearInvalid] = useState(false);
  const [partATouched, setPartATouched] = useState(false);
  const [partBError, setPartBError] = useState<string>();
  const [partBTouched, setPartBTouched] = useState(false);
  const [medicareNumberError, setMedicareNumberError] = useState<string | null>(
    null
  );
  const [medicareNumberTouched, setMedicareNumberTouched] = useState(false);
  const [ssnError, setSsnError] = useState<string | undefined>(undefined);
  const [validForm, setValidForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const alertDiv = useRef<HTMLDivElement>(null);
  const [mbiApiValid, setMbiApiValid] = useState(true);

  const {
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  } = useFlags();

  const outsideOpenEnrollment = isOutsideOpenEnrollment({
    mctYpPublicPreview,
    mctYpOpenEnrollment,
    mctYpOeNextYear,
    mctYpOutsideOpenEnrollment,
  });

  const toggleHelpDrawer = (): void => {
    // istanbul ignore next: tested elsewhere
    sendTealiumEvent("Enroll Form", "Check Card");
    setHelpDrawerOpen(!helpDrawerOpen);
  };

  const validateForm = (): void => {
    const formStatus = getValidationObject(
      enrollData,
      plan,
      EnrollmentPages.MedicareInfo
    );

    if (
      medicareNumberTouched &&
      (!mbiApiValid || !formStatus.medicareNumberValidity)
    ) {
      setMedicareNumberError(t("enroll_form.enter_valid_medicare"));
    } else {
      setMedicareNumberError(null);
    }

    if (submitted && !formStatus.ssnValidity) {
      setSsnError(t("enroll_form.enter_valid_ssn"));
    } else {
      setSsnError(undefined);
    }

    const { partAStart } = enrollData;
    const { day: aDay, year: aYear, month: aMonth } = partAStart;
    const partAFilled = !!aDay || !!aYear || !!aMonth;

    if (
      submitted &&
      ((partAFilled && !formStatus.partAStartValidity) ||
        (!formStatus.partAStartValidity && !formStatus.partBStartValidity))
    ) {
      setPartAError(t("enroll_form.enter_valid_start_coverage_date"));
    } else {
      setPartAError(undefined);
    }

    const { partBStart } = enrollData;
    const { day: bDay, year: bYear, month: bMonth } = partBStart;
    const partBFilled = !!bDay || !!bYear || !!bMonth;

    if (
      submitted &&
      ((partBFilled && !formStatus.partBStartValidity) ||
        (!formStatus.partAStartValidity && !formStatus.partBStartValidity))
    ) {
      setPartBError(t("enroll_form.enter_valid_start_coverage_date"));
    } else {
      setPartBError(undefined);
    }

    setValidForm(formStatus.isValid);
  };

  useEffect(() => {
    validateForm();
  }, [partATouched, partBTouched, medicareNumberTouched, mbiApiValid]);

  useEffect(() => {
    if (submitted && !validForm) {
      if (alertDiv.current) {
        alertDiv.current.scrollIntoView();
      }
    }
  }, [submitted]);

  const next = (): void => {
    setSubmitted(true);

    if (!validForm || !mbiApiValid) {
      setPartATouched(true);
      setPartBTouched(true);
      setMedicareNumberTouched(true);

      return;
    }

    nextPage();
  };

  const checkDatePiecesValidity = (): void => {
    const { day: dayA, month: monthA, year: yearA } = enrollData.partAStart;
    setPartADayInvalid(isInvalidDatePart.day(dayA));
    setPartAMonthInvalid(isInvalidDatePart.month(monthA));
    setPartAYearInvalid(isInvalidDatePart.year(yearA));

    const { day: dayB, month: monthB, year: yearB } = enrollData.partBStart;
    setPartBDayInvalid(isInvalidDatePart.day(dayB));
    setPartBMonthInvalid(isInvalidDatePart.month(monthB));
    setPartBYearInvalid(isInvalidDatePart.year(yearB));
  };

  useEffect(() => {
    validateForm();
    checkDatePiecesValidity();
  }, [enrollData]);

  useEffect(() => {
    const { medicareNumber } = enrollData;

    // If we aren't allowing HICNs, then the only valid Medicare number is MBI
    // Send the MBI to the API for secondary validation
    if (isValidMedicareNumber(medicareNumber)) {
      setMbiApiValid(false);
      validateMbi(medicareNumber).then(res => setMbiApiValid(res.valid));
    }
  }, [enrollData.medicareNumber]);

  useEffect(() => {
    if (partADayInvalid || partAMonthInvalid || partAYearInvalid)
      setPartAError(t("enroll_form.enter_valid_start_coverage_date"));
  }, [partADayInvalid, partAMonthInvalid, partAYearInvalid]);

  useEffect(() => {
    if (partBDayInvalid || partBMonthInvalid || partBYearInvalid)
      setPartBError(t("enroll_form.enter_valid_start_coverage_date"));
  }, [partBDayInvalid, partBMonthInvalid, partBYearInvalid]);

  const commonDateFieldProps = {
    className: "ds-u-margin-bottom--2",
    dateFormatter,
    hint: t("dob.hint"),
    dayLabel: t("dob.day_label"),
    monthLabel: t("dob.month_label"),
    yearLabel: t("dob.year_label"),
  };

  return (
    <div>
      <FocusableHeader
        className="EnrollmentFormPage__header"
        text={t("enrollment_form.your_medicare_info")}
      />

      <div className="ds-u-measure--wide">
        <PlanInfo plan={plan} />

        <div ref={alertDiv}>
          {!validForm && submitted && (
            <Alert
              className="ds-u-margin-top--2"
              heading={t("enroll_form.needs_your_attention")}
              variation="error"
            >
              {t("enroll_form.complete_required")}
            </Alert>
          )}
        </div>

        <section>
          <TextField
            name="medicareNumber"
            labelClassName="e2e-enroll-medicare-number ds-c-label--input"
            errorMessage={medicareNumberError}
            disabled={
              state.beneficiary && !!state.beneficiary.mbi && !state.csr
            }
            label={t("enroll_form.medicare_number")}
            value={enrollData.medicareNumber}
            onBlur={(e: ChangeEvent<HTMLInputElement>): void => {
              const medicareNumber = e.target.value
                .trim()
                .replace(/-/g, "")
                .toUpperCase();

              setEnrollData({
                ...enrollData,
                medicareNumber,
              });

              setMedicareNumberTouched(true);
            }}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => {
              setEnrollData({
                ...enrollData,
                medicareNumber: e.target.value.trim(),
              });
            }}
            hint={
              <HelpDrawerToggle
                helpDrawerOpen={helpDrawerOpen}
                showDrawer={toggleHelpDrawer}
                className="MedicareHelpDrawer__hint"
              >
                {t("medicare_help_drawer.where_to_find")}
              </HelpDrawerToggle>
            }
          />

          {helpDrawerOpen && (
            <MedicareCardHelpDrawer onCloseClick={toggleHelpDrawer} />
          )}

          {plan.snp_type === PlanSNPType.SNP_TYPE_DUAL_ELIGIBLE && (
            <SSNInput
              label={t("enroll_form.social_security_number")}
              value={enrollData.ssn}
              errorMessage={ssnError}
              onChange={(ssn: SSN): void => {
                setEnrollData({ ...enrollData, ssn: ssn });
              }}
            />
          )}

          <p className="ds-u-font-weight--semibold">
            {t("enroll_form.enter_start_date")}
          </p>

          <p>{t("enroll_form.enter_at_least_one_start_date")}</p>

          <div className="DateFieldContainer">
            <DateField
              {...commonDateFieldProps}
              errorMessage={partAError}
              label={t("enroll_form.part_a_coverage_starts")}
              onChange={(
                _: ChangeEvent<HTMLInputElement>,
                date: MCTDate
              ): void => {
                limitDateInputs(0);
                setEnrollData({ ...enrollData, partAStart: date });
                setPartATouched(
                  !!date.day &&
                    !!date.month &&
                    !!date.year &&
                    date.year.length === 4
                );
              }}
              dayDefaultValue={
                enrollData.partAStart && enrollData.partAStart.day
              }
              dayInvalid={partADayInvalid}
              dayName="partACoverageStartDay"
              monthDefaultValue={
                enrollData.partAStart && enrollData.partAStart.month
              }
              monthInvalid={partAMonthInvalid}
              monthName="partACoverageStartMonth"
              yearDefaultValue={
                enrollData.partAStart && enrollData.partAStart.year
              }
              yearInvalid={partAYearInvalid}
              yearName="partACoverageStartYear"
            />
          </div>
          <div className="DateFieldContainer">
            <DateField
              {...commonDateFieldProps}
              errorMessage={partBError}
              label={t("enroll_form.part_b_coverage_starts")}
              onChange={(
                _: ChangeEvent<HTMLInputElement>,
                date: MCTDate
              ): void => {
                limitDateInputs(1);
                setEnrollData({ ...enrollData, partBStart: date });
                setPartBTouched(
                  !!date.day &&
                    !!date.month &&
                    !!date.year &&
                    date.year.length === 4
                );
              }}
              dayDefaultValue={
                enrollData.partBStart && enrollData.partBStart.day
              }
              dayInvalid={partBDayInvalid}
              dayName="partBCoverageStartDay"
              monthDefaultValue={
                enrollData.partBStart && enrollData.partBStart.month
              }
              monthInvalid={partBMonthInvalid}
              monthName="partBCoverageStartMonth"
              yearDefaultValue={
                enrollData.partBStart && enrollData.partBStart.year
              }
              yearInvalid={partBYearInvalid}
              yearName="partBCoverageStartYear"
            />
          </div>
        </section>

        <section>
          <p className="ds-u-font-weight--semibold">
            {t("enroll_form.please_read_important_info")}
          </p>

          <CoverageLossWarning
            planType={plan.plan_type}
            planName={plan.name}
            category={plan.category}
            snp_type={plan.snp_type}
          />

          <p>{t("enroll_form.clicking_understand_disclaimer")}</p>

          <div>
            <ChoiceList
              type="checkbox"
              choices={[
                {
                  label: t("enroll_form.read_and_understood_page_contents"),
                  value: "understand_contents",
                  checked: enrollData.understoodCoverageLossWarning,
                },
              ]}
              errorMessage={
                submitted && !enrollData.understoodCoverageLossWarning
                  ? t("enroll_form.click_box")
                  : undefined
              }
              label=""
              name="understand"
              onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                setEnrollData({
                  ...enrollData,
                  understoodCoverageLossWarning: e.target.checked,
                });
              }}
              className="EnrollmentFormPage__checkbox"
            />
          </div>
        </section>

        <BackNextButtons
          nextPage={next}
          previousPage={previousPage}
          inReview={inReview}
          hideBackButton={!outsideOpenEnrollment}
        />
      </div>
    </div>
  );
};

export default MedicareInfo;
